import { useState, useEffect } from 'react';
import { toast } from '@/components/ui/sonner';
import { BookOpen, Plus, Trash2, Users, Calendar, MapPin, Clock, Phone, Edit2, Mail, MessageSquare, Video, PhoneCall, User, X, Check } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const FormationsBinome = ({ user, activeModule = "b2b" }) => {
  const [formations, setFormations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingFormation, setEditingFormation] = useState(null);
  
  const buildTrainingTitle = (clientValue) => `Formation binôme - ${clientValue || 'Amazon'}`;

  // Formation form data - structure avec IDs des employés sélectionnés
  const [formData, setFormData] = useState({
    selected_employees: [null, null], // IDs des employés sélectionnés
    employee_names: ['', ''],
    employee_phones: ['', ''],
    employee_emails: ['', ''],
    dates: [''],
    client: 'Amazon',
    client_custom: '',
    training_title: 'Formation binôme - Amazon',
    site_name: '',
    rdv_time: '',
    address: '',
    contact_phone: '',
    custom_text: '',
    send_email: true
  });

  const resetFormationForm = () => {
    setFormData({
      selected_employees: [null, null],
      employee_names: ['', ''],
      employee_phones: ['', ''],
      employee_emails: ['', ''],
      dates: [''],
      client: 'Amazon',
      client_custom: '',
      training_title: 'Formation binôme - Amazon',
      site_name: '',
      rdv_time: '',
      address: '',
      contact_phone: '',
      custom_text: '',
      send_email: true
    });
    setEditingFormation(null);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [formationsRes, empRes] = await Promise.all([
        axios.get(`${API}/formations-binome`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true })
      ]);
      setFormations(formationsRes.data);
      setEmployees((empRes.data || []).filter(e => e.role === 'employee'));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Formation handlers
  const handleAddEmployee = () => {
    setFormData(prev => ({
      ...prev,
      selected_employees: [...prev.selected_employees, null],
      employee_names: [...prev.employee_names, ''],
      employee_phones: [...prev.employee_phones, ''],
      employee_emails: [...prev.employee_emails, '']
    }));
  };

  const handleRemoveEmployee = (index) => {
    setFormData(prev => ({
      ...prev,
      selected_employees: prev.selected_employees.filter((_, i) => i !== index),
      employee_names: prev.employee_names.filter((_, i) => i !== index),
      employee_phones: prev.employee_phones.filter((_, i) => i !== index),
      employee_emails: prev.employee_emails.filter((_, i) => i !== index)
    }));
  };

  // Sélection d'un employé depuis la liste déroulante
  const handleSelectEmployee = (index, employeeId) => {
    const emp = employees.find(e => e.user_id === employeeId);
    if (emp) {
      const fullName = `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.name;
      setFormData(prev => ({
        ...prev,
        selected_employees: prev.selected_employees.map((id, i) => i === index ? employeeId : id),
        employee_names: prev.employee_names.map((name, i) => i === index ? fullName : name),
        employee_phones: prev.employee_phones.map((phone, i) => i === index ? (emp.phone || '') : phone),
        employee_emails: prev.employee_emails.map((email, i) => i === index ? (emp.email || '') : email)
      }));
    } else {
      // Reset si aucun employé sélectionné
      setFormData(prev => ({
        ...prev,
        selected_employees: prev.selected_employees.map((id, i) => i === index ? null : id),
        employee_names: prev.employee_names.map((name, i) => i === index ? '' : name),
        employee_phones: prev.employee_phones.map((phone, i) => i === index ? '' : phone),
        employee_emails: prev.employee_emails.map((email, i) => i === index ? '' : email)
      }));
    }
  };

  const handleEmployeeChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      employee_names: prev.employee_names.map((name, i) => i === index ? value : name)
    }));
  };

  const handlePhoneChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      employee_phones: prev.employee_phones.map((phone, i) => i === index ? value : phone)
    }));
  };

  const handleEmailChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      employee_emails: prev.employee_emails.map((email, i) => i === index ? value : email)
    }));
  };

  const handleAddDate = () => {
    setFormData(prev => ({
      ...prev,
      dates: [...prev.dates, '']
    }));
  };

  const handleRemoveDate = (index) => {
    setFormData(prev => ({
      ...prev,
      dates: prev.dates.filter((_, i) => i !== index)
    }));
  };

  const handleDateChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      dates: prev.dates.map((date, i) => i === index ? value : date)
    }));
  };

  const handleSubmitFormation = async (e) => {
    e.preventDefault();
    try {
      // Filtrer les employés valides (ceux avec un nom)
      const validEmployeeIndices = formData.employee_names
        .map((n, i) => n?.trim() ? i : -1)
        .filter(i => i >= 0);

      const resolvedClient = formData.client === 'Autre'
        ? (formData.client_custom || '').trim()
        : formData.client;

      if (!resolvedClient) {
        alert('Le client est obligatoire');
        return;
      }

      if (!formData.training_title?.trim()) {
        alert('L\'intitulé de la formation est obligatoire');
        return;
      }
      
      const dataToSend = {
        ...formData,
        client: resolvedClient,
        training_title: formData.training_title.trim(),
        employee_names: validEmployeeIndices.map(i => formData.employee_names[i]),
        employee_phones: validEmployeeIndices.map(i => formData.employee_phones[i] || ''),
        employee_emails: validEmployeeIndices.map(i => formData.employee_emails[i] || ''),
        dates: formData.dates.filter(d => d),
        address: formData.address || null,
        contact_phone: formData.contact_phone || null,
        custom_text: formData.custom_text || null,
        send_email: formData.send_email
      };

      // Validation des champs obligatoires si envoi email activé
      if (dataToSend.send_email && !editingFormation) {
        const missingFields = [];
        if (!dataToSend.rdv_time) missingFields.push("Heure de rendez-vous");
        if (!dataToSend.address) missingFields.push("Adresse complète");
        if (!dataToSend.contact_phone) missingFields.push("Numéro du responsable");
        if (!dataToSend.custom_text) missingFields.push("Texte personnalisé");
        
        // Vérifier qu'au moins un employé a un email
        const employeesWithEmail = dataToSend.employee_emails.filter(e => e?.trim()).length;
        if (employeesWithEmail === 0) {
          missingFields.push("Au moins un employé avec email");
        }
        
        if (missingFields.length > 0) {
          alert(`Champs obligatoires pour l'envoi d'email:\n${missingFields.join('\n')}`);
          return;
        }
      }

      if (editingFormation) {
        // Demander si on veut envoyer un email de modification
        const sendModificationEmail = window.confirm(
          'Des modifications importantes ont été apportées.\n\nVoulez-vous envoyer un email de modification aux participants ?'
        );
        await axios.put(
          `${API}/formations-binome/${editingFormation.formation_id}?send_modification_email=${sendModificationEmail}`, 
          dataToSend, 
          { withCredentials: true }
        );
        if (sendModificationEmail) {
          alert('Formation mise à jour et emails de modification envoyés.');
        }
      } else {
        const response = await axios.post(`${API}/formations-binome`, dataToSend, { withCredentials: true });
        if (response.data.emails_sent > 0) {
          alert(`Formation créée! ${response.data.emails_sent} email(s) envoyé(s).`);
        }
      }
      
      fetchAll();
      setShowForm(false);
      resetFormationForm();
    } catch (error) {
      console.error('Failed to save formation:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleEditFormation = (formation) => {
    // Essayer de retrouver les IDs des employés par leur nom/email
    const selectedEmployees = (formation.employee_names || []).map((name, idx) => {
      const email = formation.employee_emails?.[idx];
      // Chercher par email d'abord (plus fiable), puis par nom
      const emp = employees.find(e => 
        (email && e.email === email) || 
        `${e.first_name || ''} ${e.last_name || ''}`.trim().toLowerCase() === name.toLowerCase() ||
        e.name?.toLowerCase() === name.toLowerCase()
      );
      return emp?.user_id || null;
    });
    
    setFormData({
      selected_employees: selectedEmployees.length >= 2 ? selectedEmployees : [null, null],
      employee_names: formation.employee_names?.length >= 2 ? formation.employee_names : ['', ''],
      employee_phones: formation.employee_phones?.length >= 2 ? formation.employee_phones : ['', ''],
      employee_emails: formation.employee_emails?.length >= 2 ? formation.employee_emails : ['', ''],
      dates: formation.dates || [''],
      client: (formation.client === 'Amazon' || formation.client === 'Colis Privé') ? formation.client : 'Autre',
      client_custom: (formation.client && formation.client !== 'Amazon' && formation.client !== 'Colis Privé') ? formation.client : '',
      training_title: formation.training_title || buildTrainingTitle(formation.client || 'Amazon'),
      site_name: formation.site_name || '',
      rdv_time: formation.rdv_time || '',
      address: formation.address || '',
      contact_phone: formation.contact_phone || '',
      custom_text: formation.custom_text || '',
      send_email: false // Pas d'email lors de la modification
    });
    setEditingFormation(formation);
    setShowForm(true);
  };

  const handleDeleteFormation = async (formationId) => {
    if (!window.confirm('Supprimer cette formation ?')) return;
    try {
      await axios.delete(`${API}/formations-binome/${formationId}`, { withCredentials: true });
      fetchAll();
    } catch (error) {
      console.error('Failed to delete formation:', error);
      toast.error(`Formation non supprimée : ${typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'vérifiez la connexion puis réessayez'}`);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Layout user={user} activeModule={activeModule}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6 pb-20">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#0F172A] font-['Chivo'] flex items-center gap-3">
              <BookOpen className="w-8 h-8" />
              Formation Binôme
            </h1>
            <p className="text-gray-500 mt-1">
              {formations.length} formation(s) planifiée(s)
            </p>
          </div>
          <Button
            onClick={() => {
              setShowForm(true);
              resetFormationForm();
            }}
            className="bg-[#0F172A] hover:bg-[#1e293b]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle formation
          </Button>
        </div>

        {/* Formations list */}
        {formations.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">Aucune formation planifiée</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {formations.map((formation) => (
              <Card key={formation.formation_id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        <span className="font-semibold text-[#0F172A]">{formation.training_title || buildTrainingTitle(formation.client || 'Amazon')}</span>
                        <span className="text-sm text-gray-500">• {formation.site_name}</span>
                        {formation.rdv_time && (
                          <span className="flex items-center gap-1 text-sm text-gray-500">
                            <Clock className="w-3 h-3" /> {formation.rdv_time}
                          </span>
                        )}
                      </div>
                      
                      {/* Adresse et téléphone responsable */}
                      {(formation.address || formation.contact_phone) && (
                          <div className="flex flex-wrap gap-3 mb-2 text-sm text-gray-600">
                            {formation.address && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-blue-500" />
                                {formation.address}
                              </span>
                            )}
                            {formation.contact_phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3 text-green-500" />
                                {formation.contact_phone}
                              </span>
                            )}
                          </div>
                        )}
                        
                        <div className="flex flex-wrap gap-2 mb-3">
                          {formation.employee_names?.map((name, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-full">
                              <User className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-blue-700">{name}</span>
                              {formation.employee_phones?.[idx] && (
                                <span className="text-xs text-blue-500 flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {formation.employee_phones[idx]}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          {formation.dates?.map((date, idx) => (
                            <span key={idx} className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {formatDate(date)}
                            </span>
                          ))}
                        </div>
                        
                        {/* Texte personnalisé */}
                        {formation.custom_text && (
                          <div className="mt-2 p-2 bg-yellow-50 rounded text-sm text-gray-700 italic">
                            {formation.custom_text}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditFormation(formation)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteFormation(formation.formation_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
        )}

        {/* Modal Form */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-[#0F172A]">
                  {editingFormation ? 'Modifier la formation' : 'Nouvelle formation'}
                </h2>
                <button
                  onClick={() => {
                    setShowForm(false);
                    resetFormationForm();
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Formation Form */}
              <form onSubmit={handleSubmitFormation} className="p-4 space-y-4">
                  <div>
                    <Label className="font-semibold">Client *</Label>
                    <select
                      value={formData.client}
                      onChange={(e) => {
                        const nextClient = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          client: nextClient,
                          client_custom: nextClient === 'Autre' ? prev.client_custom : '',
                          training_title: prev.training_title === buildTrainingTitle(prev.client) || !prev.training_title
                            ? buildTrainingTitle(nextClient)
                            : prev.training_title
                        }));
                      }}
                      className="w-full h-10 px-3 border border-gray-300 rounded-md bg-white mt-1"
                      required
                      data-testid="binome-client-select"
                    >
                      <option value="Amazon">Amazon</option>
                      <option value="Colis Privé">Colis Privé</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>

                  {formData.client === 'Autre' && (
                    <div>
                      <Label className="font-semibold">Nom du client (autre) *</Label>
                      <Input
                        value={formData.client_custom || ''}
                        onChange={(e) => {
                          const customClient = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            client_custom: customClient,
                            training_title: prev.training_title === buildTrainingTitle(prev.client_custom || 'Autre') || prev.training_title === buildTrainingTitle(prev.client) || !prev.training_title
                              ? buildTrainingTitle(customClient || 'Autre')
                              : prev.training_title
                          }));
                        }}
                        placeholder="Ex: STSA / IDF4"
                        className="mt-1"
                        required
                        data-testid="binome-client-custom-input"
                      />
                    </div>
                  )}

                  <div>
                    <Label className="font-semibold">Intitulé de formation *</Label>
                    <Input
                      value={formData.training_title}
                      onChange={(e) => setFormData(prev => ({ ...prev, training_title: e.target.value }))}
                      placeholder="Ex: Formation binôme - Colis Privé"
                      required
                      className="mt-1"
                      data-testid="binome-training-title-input"
                    />
                  </div>

                  <div>
                    <Label className="font-semibold">Site de formation</Label>
                    <Input
                      value={formData.site_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, site_name: e.target.value }))}
                      placeholder="Ex: AMZL DIF4"
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label className="font-semibold">Heure de rendez-vous</Label>
                    <Input
                      type="time"
                      value={formData.rdv_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, rdv_time: e.target.value }))}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="font-semibold">Employés en formation</Label>
                      <Button type="button" variant="outline" size="sm" onClick={handleAddEmployee}>
                        <Plus className="w-4 h-4 mr-1" /> Ajouter
                      </Button>
                    </div>
                    {formData.selected_employees.map((selectedId, index) => (
                      <div key={index} className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-gray-500">Employé {index + 1}</span>
                          {formData.selected_employees.length > 2 && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveEmployee(index)} className="ml-auto">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                        
                        {/* Liste déroulante des employés */}
                        <select
                          value={selectedId || ''}
                          onChange={(e) => handleSelectEmployee(index, e.target.value)}
                          className="w-full h-10 px-3 border border-gray-300 rounded-md bg-white mb-2"
                        >
                          <option value="">-- Sélectionner un employé --</option>
                          {employees
                            .filter(emp => emp.active !== false && emp.status === 'approved')
                            .sort((a, b) => {
                              const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.name;
                              const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim() || b.name;
                              return nameA.localeCompare(nameB);
                            })
                            .map(emp => {
                              const fullName = `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.name;
                              return (
                                <option key={emp.user_id} value={emp.user_id}>
                                  {fullName} {emp.email ? `(${emp.email})` : ''}
                                </option>
                              );
                            })}
                        </select>
                        
                        {/* Infos de l'employé sélectionné (lecture seule) */}
                        {selectedId && (
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="flex items-center gap-1 text-gray-600">
                              <User className="w-3 h-3" />
                              <span>{formData.employee_names[index] || '-'}</span>
                            </div>
                            <div className="flex items-center gap-1 text-gray-600">
                              <Phone className="w-3 h-3" />
                              <span>{formData.employee_phones[index] || '-'}</span>
                            </div>
                            <div className="flex items-center gap-1 text-blue-600 col-span-2">
                              <Mail className="w-3 h-3" />
                              <span>{formData.employee_emails[index] || 'Pas d\'email'}</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Avertissement si pas d'email */}
                        {selectedId && !formData.employee_emails[index] && formData.send_email && (
                          <p className="text-xs text-orange-600 mt-2">
                            ⚠️ Cet employé n'a pas d'email enregistré
                          </p>
                        )}
                      </div>
                    ))}
                    
                    {formData.send_email && formData.employee_emails.filter(e => e?.trim()).length === 0 && (
                      <p className="text-xs text-red-600 mt-1 p-2 bg-red-50 rounded">
                        ⚠️ Aucun employé avec email sélectionné - Les convocations ne pourront pas être envoyées
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="font-semibold">Dates de formation</Label>
                      <Button type="button" variant="outline" size="sm" onClick={handleAddDate}>
                        <Plus className="w-4 h-4 mr-1" /> Ajouter
                      </Button>
                    </div>
                    {formData.dates.map((date, index) => (
                      <div key={index} className="flex gap-2 mb-2">
                        <Input
                          type="date"
                          value={date}
                          onChange={(e) => handleDateChange(index, e.target.value)}
                          className="flex-1"
                        />
                        {formData.dates.length > 1 && (
                          <Button type="button" variant="outline" size="sm" onClick={() => handleRemoveDate(index)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Nouveaux champs pour formation binôme */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-semibold text-sm text-gray-700 mb-3">Informations complémentaires</h4>
                    
                    <div className="mb-3">
                      <Label className="text-sm">Adresse complète du RDV</Label>
                      <Input
                        value={formData.address}
                        onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                        placeholder="Ex: 123 Rue du Commerce, 75001 Paris"
                        className="mt-1"
                      />
                    </div>
                    
                    <div className="mb-3">
                      <Label className="text-sm">Numéro du responsable à contacter</Label>
                      <Input
                        value={formData.contact_phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                        placeholder="Ex: 06 12 34 56 78"
                        className="mt-1"
                      />
                    </div>
                    
                    <div className="mb-3">
                      <Label className="text-sm">Texte personnalisé (instructions spécifiques)</Label>
                      <textarea
                        value={formData.custom_text}
                        onChange={(e) => setFormData(prev => ({ ...prev, custom_text: e.target.value }))}
                        placeholder="Instructions particulières pour cette formation..."
                        className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-md text-sm"
                        rows={3}
                      />
                    </div>
                    
                    <div className="flex items-center gap-2 mt-4 p-3 bg-blue-50 rounded-lg">
                      <input
                        type="checkbox"
                        id="send_email"
                        checked={formData.send_email}
                        onChange={(e) => setFormData(prev => ({ ...prev, send_email: e.target.checked }))}
                        className="w-4 h-4"
                      />
                      <label htmlFor="send_email" className="text-sm">
                        <Mail className="w-4 h-4 inline mr-1 text-blue-600" />
                        Envoyer automatiquement un email aux employés
                      </label>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button type="submit" className="flex-1 bg-[#0F172A]">
                      {editingFormation ? 'Mettre à jour' : 'Créer la formation'}
                    </Button>
                  </div>
                </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FormationsBinome;
